import { Inject, Injectable, Optional } from '@angular/core';
import { APP_BASE_HREF, Location, LocationChangeListener, LocationStrategy, PlatformLocation } from '@angular/common';

/**
 * Custom LocationStrategy for MFE apps running inside iframes.
 *
 * Key behavior: pushState() and replaceState() are complete no-ops for
 * the browser — they only update an in-memory virtual path. This means
 * the MFE NEVER touches the iframe's browser history entries. The parent
 * (PaymentServicesGW.aspx) manages all browser history via the
 * psg:mfeRouteChange / psg:navigate event bridge.
 *
 * Why this matters:
 * PSG (the host Angular app) pushes iframe history entries when it
 * navigates between tabs (e.g. /taxfiling, /employeepay). If the MFE
 * calls replaceState(), it overwrites that iframe entry's URL with an
 * MFE-internal route. When the browser Back button later pops that
 * entry, PSG sees an unrecognized URL and falls through to its wildcard
 * redirect — skipping the expected landing page.
 */
@Injectable()
export class MultiLocationStrategy extends LocationStrategy {
  private _baseHref: string;

  /**
   * In-memory path that Angular's router reads via path().
   * Updated by pushState/replaceState but never written to browser history.
   */
  private _virtualPath = '';
  private _virtualState: unknown = null;

  constructor(private _platformLocation: PlatformLocation,
    @Optional() @Inject(APP_BASE_HREF) href?: string) {
    super();

    if (href == null) {
      href = this._platformLocation.getBaseHrefFromDOM();
    }

    if (href == null) {
      throw new Error(
        `No base href set. Please provide a value for the APP_BASE_HREF token or add a base element to the document.`);
    }

    this._baseHref = href;
  }

  onPopState(fn: LocationChangeListener): void {
    // No-op: back navigation is handled via psg:navigate events from the
    // parent frame, routed through HostLocationService.
  }

  getBaseHref(): string {
    return this._baseHref;
  }

  prepareExternalUrl(internal: string, state?: any): string {
    return internal;
  }

  path(includeHash: boolean = false): string {
    // Return the virtual path if we've navigated at least once.
    // On initial load (before any navigation), fall back to the browser
    // pathname so Angular's router can read the initial URL for bootstrapping.
    if (this._virtualPath) {
      return this._virtualPath;
    }
    const pathname = this._platformLocation.pathname +
      Location.normalizeQueryParams(this._platformLocation.search);
    const hash = this._platformLocation.hash;
    return hash && includeHash ? `${pathname}${hash}` : pathname;
  }

  /**
   * No-op for browser history. Only updates the in-memory virtual path
   * so Angular's router can track the current route internally.
   * The parent window manages real browser history via psg:mfeRouteChange events.
   */
  pushState(state: any, title: string, url: string, queryParams: string): void {
    this._virtualPath = this.prepareExternalUrl(url + Location.normalizeQueryParams(queryParams), state);
    this._virtualState = state;
  }

  replaceState(state: any, title: string, url: string, queryParams: string): void {
    this._virtualPath = this.prepareExternalUrl(url + Location.normalizeQueryParams(queryParams), state);
    this._virtualState = state;
  }

  forward(): void {
    // No-op: parent manages browser history
  }

  back(): void {
    // No-op: parent manages browser history
  }

  getState(): unknown {
    return this._virtualState;
  }
}
